import {DateTime} from "luxon";
import {type FilterConfiguration, type FilterConfigurationLineItem} from "~/components/ottopilotDataViewer/filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import type {Iso8601DateTime} from "@vesta/common-type-definitions/typeDefinitions";
import {
    inclusiveCalendarDateRangeToHalfOpenIsoBounds,
    inclusiveIsoDateTimeToExclusiveIsoDateTime,
    parseCalendarDateFromIsoString,
} from "~/utilities/calendarDateIso";
import {getDefaultLast30DaysDateRangeIsoBounds} from "~/utilities/defaultDateRange";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";

function isDateRangeUpperBoundOperation(operation: FilterOperation): boolean {
    return operation === FilterOperation.lessThan || operation === FilterOperation.lessThanEqualTo;
}

function normalizeCreatedAtDateRangeBounds(
    range: [Iso8601DateTime | null, Iso8601DateTime | null],
): [Iso8601DateTime | null, Iso8601DateTime | null] {
    const start = range[0];
    let end = range[1];
    if (start === null) {
        return range;
    }
    if (end === null) {
        end = start;
    }

    const bounds = inclusiveCalendarDateRangeToHalfOpenIsoBounds({start, end});
    if (bounds === null) {
        return [start, end];
    }

    return [bounds.fromInclusive, bounds.toExclusive];
}

/** Column id for the shared created-at date range filter pill. */
export const DATA_VIEWER_CREATED_AT_DATE_RANGE_COLUMN_ID = "createdAt";

export function getDefaultCreatedAtDateRangeIsoBounds(): {from: string; to: string} {
    const {start, end} = getDefaultLast30DaysDateRangeIsoBounds();
    return {from: start, to: end};
}

export function getDefaultLast30DaysDateRange(): [Iso8601DateTime, Iso8601DateTime] {
    const {from, to} = getDefaultCreatedAtDateRangeIsoBounds();
    return [from as Iso8601DateTime, to as Iso8601DateTime];
}

export function buildDefaultCreatedAtDateRangeFilterLineItems(
    columnId: string = DATA_VIEWER_CREATED_AT_DATE_RANGE_COLUMN_ID,
): Array<FilterConfigurationLineItem> {
    const [start, end] = normalizeCreatedAtDateRangeBounds(getDefaultLast30DaysDateRange());
    return [
        {
            columnId,
            operation: FilterOperation.greaterThanEqualTo,
            value: start,
        },
        {
            columnId,
            operation: FilterOperation.lessThan,
            value: end,
        },
    ];
}

export function hasDateRangeFilterOnColumn(
    lineItems: FilterConfiguration["lineItems"],
    columnId: string,
): boolean {
    return lineItems.some(
        (lineItem) =>
            lineItem.columnId === columnId &&
            (lineItem.operation === FilterOperation.greaterThanEqualTo ||
                isDateRangeUpperBoundOperation(lineItem.operation)),
    );
}

export function isCreatedAtDateRangeFilterLineItem(
    lineItem: FilterConfigurationLineItem,
    columnId: string = DATA_VIEWER_CREATED_AT_DATE_RANGE_COLUMN_ID,
): boolean {
    return (
        lineItem.columnId === columnId &&
        (lineItem.operation === FilterOperation.greaterThanEqualTo ||
            isDateRangeUpperBoundOperation(lineItem.operation))
    );
}

/**
 * True when the applied created-at range is still the default rolling last-30-days window
 * (not a range chosen via the calendar picker).
 */
export function isDefaultCreatedAtDateRangeFilters(
    lineItems: FilterConfiguration["lineItems"],
    columnId: string = DATA_VIEWER_CREATED_AT_DATE_RANGE_COLUMN_ID,
): boolean {
    const [start, end] = readCreatedAtDateRangeFromFilters(lineItems, columnId);
    if (start === null || end === null) {
        return false;
    }

    const {from} = getDefaultCreatedAtDateRangeIsoBounds();
    const startDt = DateTime.fromISO(start, {setZone: true});
    const fromDt = DateTime.fromISO(from, {setZone: true});
    if (startDt.isValid !== true || fromDt.isValid !== true) {
        return false;
    }
    if (startDt.startOf("day").toMillis() !== fromDt.startOf("day").toMillis()) {
        return false;
    }

    const endDt = DateTime.fromISO(end, {setZone: true});
    return endDt.isValid === true && endDt.hasSame(DateTime.now(), "day");
}

export function readCreatedAtDateRangeFromFilters(
    lineItems: FilterConfiguration["lineItems"],
    columnId: string = DATA_VIEWER_CREATED_AT_DATE_RANGE_COLUMN_ID,
): [Iso8601DateTime | null, Iso8601DateTime | null] {
    const fromFilter = lineItems.find(
        (lineItem) =>
            lineItem.columnId === columnId && lineItem.operation === FilterOperation.greaterThanEqualTo,
    );
    const toFilter = lineItems.find(
        (lineItem) =>
            lineItem.columnId === columnId && isDateRangeUpperBoundOperation(lineItem.operation),
    );

    const start =
        fromFilter !== undefined && "value" in fromFilter && fromFilter.value !== null
            ? (fromFilter.value as Iso8601DateTime)
            : null;
    let end =
        toFilter !== undefined && "value" in toFilter && toFilter.value !== null
            ? (toFilter.value as Iso8601DateTime)
            : null;
    if (end !== null && toFilter?.operation === FilterOperation.lessThan) {
        const exclusiveEnd = parseCalendarDateFromIsoString(end);
        if (exclusiveEnd !== null) {
            end = (exclusiveEnd.minus({days: 1}).startOf("day").toISO() ?? end) as Iso8601DateTime;
        }
    }

    return [start, end];
}

/** Raw `[from, to)` bounds as stored on the filter (exclusive end), or `null` if incomplete. */
export function readHalfOpenDateRangeFromFilters(
    lineItems: FilterConfiguration["lineItems"],
    columnId: string = DATA_VIEWER_CREATED_AT_DATE_RANGE_COLUMN_ID,
): {rangeStart: Iso8601DateTime; rangeEnd: Iso8601DateTime} | null {
    const fromFilter = lineItems.find(
        (lineItem) =>
            lineItem.columnId === columnId && lineItem.operation === FilterOperation.greaterThanEqualTo,
    );
    const toFilter = lineItems.find(
        (lineItem) =>
            lineItem.columnId === columnId && isDateRangeUpperBoundOperation(lineItem.operation),
    );
    if (
        fromFilter === undefined ||
        toFilter === undefined ||
        !("value" in fromFilter) ||
        !("value" in toFilter) ||
        fromFilter.value === null ||
        toFilter.value === null
    ) {
        return null;
    }

    const rangeStart = fromFilter.value as Iso8601DateTime;
    const rangeEnd =
        toFilter.operation === FilterOperation.lessThanEqualTo
            ? inclusiveIsoDateTimeToExclusiveIsoDateTime(toFilter.value as Iso8601DateTime)
            : (toFilter.value as Iso8601DateTime);
    return {
        rangeStart: rangeStart,
        rangeEnd: rangeEnd,
    };
}

export function applyCreatedAtDateRangeToFilters(
    filterConfiguration: FilterConfiguration,
    range: [Iso8601DateTime | null, Iso8601DateTime | null],
    columnId: string = DATA_VIEWER_CREATED_AT_DATE_RANGE_COLUMN_ID,
): FilterConfiguration {
    const withoutDateRange = filterConfiguration.lineItems.filter(
        (lineItem) =>
            !(
                lineItem.columnId === columnId &&
                (lineItem.operation === FilterOperation.greaterThanEqualTo ||
                    isDateRangeUpperBoundOperation(lineItem.operation))
            ),
    );

    const [start, end] = normalizeCreatedAtDateRangeBounds(range);
    if (start === null || end === null) {
        return {
            revisionId: generateUuid(),
            lineItems: withoutDateRange,
        };
    }

    return {
        revisionId: generateUuid(),
        lineItems: [
            ...withoutDateRange,
            {
                columnId,
                operation: FilterOperation.greaterThanEqualTo,
                value: start,
            },
            {
                columnId,
                operation: FilterOperation.lessThan,
                value: end,
            },
        ],
    };
}

/** When false, the created-at date range pill stays visible but no default last-30-days range is applied. */
export function shouldApplyDefaultCreatedAtMonthFilter(
    defaultCreatedAtMonthFilter: boolean | undefined,
): boolean {
    return defaultCreatedAtMonthFilter !== false;
}

export function stripAutoDefaultCreatedAtDateRangeFromLineItems(
    lineItems: FilterConfiguration["lineItems"],
    applyDefaultCreatedAtMonthFilter: boolean,
    columnId: string = DATA_VIEWER_CREATED_AT_DATE_RANGE_COLUMN_ID,
): FilterConfiguration["lineItems"] {
    if (shouldApplyDefaultCreatedAtMonthFilter(applyDefaultCreatedAtMonthFilter)) {
        return lineItems;
    }
    if (isDefaultCreatedAtDateRangeFilters(lineItems, columnId) !== true) {
        return lineItems;
    }
    return lineItems.filter((lineItem) => isCreatedAtDateRangeFilterLineItem(lineItem, columnId) === false);
}

export function stripDateRangeFilterLineItemsFromFilterConfiguration(
    filterConfiguration: FilterConfiguration,
    columnIds: Array<string>,
): FilterConfiguration {
    const columnIdSet = new Set(columnIds);
    const lineItems = filterConfiguration.lineItems.filter(
        (lineItem) =>
            !(
                columnIdSet.has(lineItem.columnId) &&
                (lineItem.operation === FilterOperation.greaterThanEqualTo ||
                    isDateRangeUpperBoundOperation(lineItem.operation))
            ),
    );
    if (lineItems.length === filterConfiguration.lineItems.length) {
        return filterConfiguration;
    }
    return {
        revisionId: generateUuid(),
        lineItems,
    };
}

export function stripCreatedAtDateRangeLineItems(
    lineItems: Array<FilterConfigurationLineItem>,
    columnId: string = DATA_VIEWER_CREATED_AT_DATE_RANGE_COLUMN_ID,
): Array<FilterConfigurationLineItem> {
    return lineItems.filter((lineItem) => isCreatedAtDateRangeFilterLineItem(lineItem, columnId) === false);
}

export function mergeDefaultCreatedAtMonthFilter(
    initial: FilterConfiguration | undefined,
    columnId: string = DATA_VIEWER_CREATED_AT_DATE_RANGE_COLUMN_ID,
    applyDefault: boolean = true,
): FilterConfiguration {
    const base: FilterConfiguration = initial ?? {
        revisionId: generateUuid(),
        lineItems: [],
    };

    if (applyDefault !== true || hasDateRangeFilterOnColumn(base.lineItems, columnId)) {
        return base;
    }

    return {
        revisionId: generateUuid(),
        lineItems: [...buildDefaultCreatedAtDateRangeFilterLineItems(columnId), ...base.lineItems],
    };
}
